import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const SimulationResults = ({ simulations }) => {
  const [activeTab, setActiveTab] = useState('supply');

  const tabs = [
    { id: 'supply', label: 'Kolaps Pasokan', icon: 'TrendingDown' },
    { id: 'cashflow', label: 'Guncangan Cashflow', icon: 'DollarSign' },
    { id: 'operational', label: 'Kegagalan Operasional', icon: 'AlertCircle' },
  ];

  const getSimulationData = () => {
    return simulations?.find(s => s?.type === activeTab);
  };

  const currentSimulation = getSimulationData();

  return (
    <div className="card bg-gradient-to-br from-card to-accent/5 shadow-xl border-2">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold mb-2 bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent">Hasil Simulasi Risiko</h2>
          <p className="text-sm md:text-base text-muted-foreground">
            Proyeksi dampak skenario risiko terhadap operasional bisnis
          </p>
        </div>
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent to-accent/60 flex items-center justify-center shadow-lg animate-pulse">
          <Icon name="Activity" size={28} className="text-accent-foreground" />
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-6 border-b-2 border-border pb-4">
        {tabs?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`px-5 py-3 rounded-xl text-sm font-semibold transition-all duration-300 flex items-center gap-2 transform hover:scale-105 ${
              activeTab === tab?.id
                ? 'bg-gradient-to-r from-primary to-accent text-primary-foreground shadow-lg shadow-primary/30'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            <Icon name={tab?.icon} size={18} />
            <span>{tab?.label}</span>
          </button>
        ))}
      </div>
      {currentSimulation && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-5 rounded-2xl bg-gradient-to-br from-error/20 to-error/5 border-2 border-error/30 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-error to-error/60 flex items-center justify-center shadow-md">
                  <Icon name="AlertTriangle" size={20} className="text-error-foreground" />
                </div>
                <span className="text-sm font-bold text-error">Probabilitas</span>
              </div>
              <div className="text-3xl font-bold data-text text-error drop-shadow-sm">
                {currentSimulation?.probability}%
              </div>
            </div>

            <div className="p-5 rounded-2xl bg-gradient-to-br from-warning/20 to-warning/5 border-2 border-warning/30 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-warning to-warning/60 flex items-center justify-center shadow-md">
                  <Icon name="Clock" size={20} className="text-warning-foreground" />
                </div>
                <span className="text-sm font-bold text-warning">Waktu Dampak</span>
              </div>
              <div className="text-3xl font-bold data-text text-warning drop-shadow-sm">
                {currentSimulation?.impactTime}
              </div>
            </div>

            <div className="p-5 rounded-2xl bg-gradient-to-br from-error/20 to-error/5 border-2 border-error/30 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-error to-error/60 flex items-center justify-center shadow-md">
                  <Icon name="TrendingDown" size={20} className="text-error-foreground" />
                </div>
                <span className="text-sm font-bold text-error">Kerugian Estimasi</span>
              </div>
              <div className="text-3xl font-bold data-text text-error drop-shadow-sm">
                {currentSimulation?.estimatedLoss}
              </div>
            </div>
          </div>

          <div className="w-full h-80 p-4 rounded-2xl bg-gradient-to-br from-muted/30 to-transparent border border-border shadow-inner" aria-label={`${currentSimulation?.title} Simulation Chart`}>
            <ResponsiveContainer width="100%" height="100%">
              {currentSimulation?.chartType === 'line' ? (
                <LineChart data={currentSimulation?.chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis 
                    dataKey="period" 
                    stroke="var(--color-muted-foreground)"
                    style={{ fontSize: '12px', fontWeight: '600' }}
                  />
                  <YAxis 
                    stroke="var(--color-muted-foreground)"
                    style={{ fontSize: '12px', fontWeight: '600' }}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'var(--color-card)',
                      border: '2px solid var(--color-border)',
                      borderRadius: '12px',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="baseline" 
                    stroke="var(--color-success)" 
                    strokeWidth={3}
                    name="Baseline Normal"
                    dot={{ r: 5, fill: 'var(--color-success)' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="scenario" 
                    stroke="var(--color-error)" 
                    strokeWidth={3}
                    name="Skenario Risiko"
                    dot={{ r: 5, fill: 'var(--color-error)' }}
                  />
                </LineChart>
              ) : (
                <BarChart data={currentSimulation?.chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis 
                    dataKey="category" 
                    stroke="var(--color-muted-foreground)"
                    style={{ fontSize: '12px', fontWeight: '600' }}
                  />
                  <YAxis 
                    stroke="var(--color-muted-foreground)"
                    style={{ fontSize: '12px', fontWeight: '600' }}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'var(--color-card)',
                      border: '2px solid var(--color-border)',
                      borderRadius: '12px',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Legend />
                  <Bar dataKey="impact" fill="var(--color-error)" name="Dampak Risiko" radius={[8, 8, 0, 0]} />
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>

          <div className="p-5 rounded-2xl bg-gradient-to-br from-muted/50 to-muted/20 border-2 border-border shadow-lg">
            <h4 className="font-bold text-lg mb-3 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                <Icon name="FileText" size={16} className="text-primary-foreground" />
              </div>
              Interpretasi Hasil
            </h4>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {currentSimulation?.interpretation}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimulationResults;